import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AIListIngredientMenuViewModel } from '@app/lists/model/ai-list-builder-view.model';
import { selectAIListIngredientMenuViewModel } from '@app/lists/selectors/ai-list-view-model.selector';
import { IonContent, MenuController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { FilterOption } from '@shared/components/filter-modal/filter-modal.component';
import { Product } from '@shared/models/product.model';
import { AIListItem, UpdateItemRequest } from '@usf/list-ai-types';
import { AIListService } from '@usf/ngrx-list-ai';
import { getLoadingStateOfProducts, LoadingState } from '@usf/ngrx-product';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  scan,
  Subject,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs';
import { AIListBuilderService } from '../../services/ai-list-builder.service';
import { ProductService } from '@shared/services/product/product.service';

@Component({
  selector: 'app-ai-list-ingredient-menu',
  templateUrl: './ai-list-ingredient-menu.component.html',
  styleUrls: ['./ai-list-ingredient-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiListIngredientMenuComponent implements OnInit, OnDestroy {
  @ViewChild(IonContent) content: IonContent;

  aiListIngredientMenuViewModel$: Observable<AIListIngredientMenuViewModel> =
    this.store.select(selectAIListIngredientMenuViewModel).pipe(
      switchMap(viewModel => {
        if (
          !viewModel.productNumbers ||
          viewModel.productNumbers.length === 0
        ) {
          return of({
            initialAIListProductCardViewModel:
              viewModel.initialAIListProductCardViewModel,
            loading: viewModel.loading,
            loaded: viewModel.loaded,
            searchResults: viewModel.searchResults,
            selectedSearchResult: viewModel.selectedSearchResult,
            selectedAiList: viewModel.selectedAiList,
          });
        }
        return this.productService
          .selectLoadingStateOfProducts(viewModel.productNumbers)
          .pipe(
            map(productLoadingState => {
              return {
                initialAIListProductCardViewModel:
                  viewModel.initialAIListProductCardViewModel,
                loading:
                  viewModel.loading ||
                  productLoadingState === LoadingState.loading,
                loaded:
                  viewModel.loaded &&
                  productLoadingState === LoadingState.loaded,
                searchResults: viewModel.searchResults,
                selectedSearchResult: viewModel.selectedSearchResult,
                selectedAiList: viewModel.selectedAiList,
              };
            }),
          );
      }),
    );

  filterOptions: FilterOption[] = [
    { id: 'direct', label: 'i18n.lists.direct', selected: false },
    {
      id: 'early-cutoff',
      label: 'i18n.lists.earlyCutoff',
      selected: false,
    },
    {
      id: 'discontinued',
      label: 'i18n.lists.discontinuedWhenOut',
      selected: false,
    },
    {
      id: 'exclusive',
      label: 'i18n.lists.exclusiveBrands',
      selected: false,
    },
  ];

  platformRef = PlatformEnum;
  searchQuery = '';
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  showFilterView = false;
  selectedFilterCount = 0;

  isBestSellingSearch = true;
  hasFiltersBeenSavedBefore = false;

  constructor(
    private aiListBuilderService: AIListBuilderService,
    private aiListService: AIListService,
    readonly panAppState: PanAppState,
    readonly platformService: PlatformService,
    private readonly menuController: MenuController,
    private readonly store: Store,
    private readonly productService: ProductService,
  ) {}

  ngOnInit(): void {
    this.setupDebouncedSearch();
    this.setupScrollResetOnContentLoad();
  }

  private setupScrollResetOnContentLoad(): void {
    this.aiListIngredientMenuViewModel$
      .pipe(
        takeUntil(this.destroy$),
        scan((acc, current) => ({ prev: acc.current, current }), {
          prev: null,
          current: null,
        }),
        // Only trigger when transitioning from loading to loaded
        filter(
          ({ prev, current }) =>
            prev?.loading === true &&
            current?.loading === false &&
            current?.loaded === true,
        ),
        map(({ current }) => current),
      )
      .subscribe(() => {
        // Reset scroll position when content finishes loading
        if (this.content) {
          this.content.scrollToPoint(0, 0, 0);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupDebouncedSearch(): void {
    this.searchSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        withLatestFrom(this.aiListIngredientMenuViewModel$),
        takeUntil(this.destroy$),
      )
      .subscribe(([searchQuery, viewModel]) => {
        this.performSearch(searchQuery, viewModel);
      });
  }

  onSearchInput(): void {
    this.searchSubject.next(this.searchQuery);
  }

  async onCancel(viewModel: AIListIngredientMenuViewModel): Promise<void> {
    await this.menuController.close(
      'ingredient-menu-' + viewModel.selectedAiList.aiListId,
    );
  }

  onFilter(): void {
    this.showFilterView = true;
  }

  toggleFilterOption(option: FilterOption) {
    option.selected = !option.selected;
  }

  applyFilterOptions(): void {
    const menuFilters: string[] = [];

    for (const option of this.filterOptions) {
      if (option.selected) {
        menuFilters.push(option.id);
      }
    }

    this.aiListBuilderService.updateMenuFilterOptions(menuFilters);
  }

  performSearch(
    searchQuery: string,
    viewModel: AIListIngredientMenuViewModel,
  ): void {
    const trimmedSearchQuery = searchQuery.trim();

    if (!trimmedSearchQuery && !this.isBestSellingSearch) {
      this.aiListService.searchIngredients({
        ingredientName:
          viewModel.initialAIListProductCardViewModel.ingredientDisplayName,
        pimInfo: viewModel.initialAIListProductCardViewModel.aiListItem.pimInfo,
      });
      this.isBestSellingSearch = true;
      this.clearAllFilters();
      this.saveFilterOptions();
      this.hasFiltersBeenSavedBefore = false;
      return;
    }

    if (trimmedSearchQuery.length < 3) {
      return;
    }

    const searchQueryAsNumber = Number(trimmedSearchQuery);
    const searchQueryIsNumber =
      !isNaN(searchQueryAsNumber) && searchQueryAsNumber > 0;

    if (searchQueryIsNumber) {
      this.aiListService.searchIngredientProductNumber(searchQueryAsNumber);
    } else {
      this.aiListService.searchIngredients({
        ingredientName: trimmedSearchQuery,
        pimInfo: viewModel.initialAIListProductCardViewModel.aiListItem.pimInfo,
      });
    }
    this.isBestSellingSearch = false;
    if (this.hasFiltersBeenSavedBefore) {
      this.clearAllFilters();
      this.saveFilterOptions();
      this.hasFiltersBeenSavedBefore = false;
    }
  }

  onSave(viewModel: AIListIngredientMenuViewModel): void {
    if (this.showFilterView) {
      this.saveFilterOptions();
    } else {
      const aiListItem: AIListItem = {
        ...viewModel.initialAIListProductCardViewModel.aiListItem,
        match: {
          packSize: viewModel.selectedSearchResult?.summary?.salesPackSize,
          description: viewModel.selectedSearchResult?.summary?.productDescTxtl,
          corporateProductNumber: String(
            viewModel.selectedSearchResult?.productNumber,
          ), // summary.pimProductNumber but it doesnt exist on the product summary model, just using normal product number
          productNumber: viewModel.selectedSearchResult?.productNumber,
          taxonomy:
            viewModel.selectedSearchResult?.summary?.classDescription +
            '.' +
            viewModel.selectedSearchResult?.summary?.categoryDescription +
            '.' +
            viewModel.selectedSearchResult?.summary?.groupDescription, // summary.classDescription + . + summary.categoryDescription + . + summary.groupDescription, Shouldnt need this but mapping just in case
          pimGroup: Number(viewModel.selectedSearchResult?.summary?.groupCode),
          brand: viewModel.selectedSearchResult?.summary?.brand,
          exclusiveBrand:
            viewModel.selectedSearchResult?.summary?.isExclusiveBrand,
          usfDirect: viewModel.selectedSearchResult?.inventory?.isDirect,
          jit: viewModel.selectedSearchResult?.inventory?.justInTime,
          dwo: viewModel.selectedSearchResult?.inventory?.productStatus === '8',
          matchUpdateDtm: null, //Being reset in the backend
        },
      };
      const request: UpdateItemRequest = {
        aiListId: viewModel.selectedAiList.aiListId,
        updateDtm: viewModel.selectedAiList.updateDtm,
        ingredientIndex:
          viewModel.initialAIListProductCardViewModel.aiListItem
            .ingredientIndex,
        groupIndex:
          viewModel.initialAIListProductCardViewModel.aiListItem.groupIndex,
        groupName:
          viewModel.initialAIListProductCardViewModel.aiListItem.groupName,
        aiListItem: aiListItem,
      };
      this.aiListService.updateAiListItem(request);
      this.menuController.close(
        'ingredient-menu-' + viewModel.selectedAiList.aiListId,
      );
    }
  }

  shouldEnableSaveInFilterView(): boolean {
    if (this.filterOptions.some(option => option.selected)) {
      return true;
    }

    // If no options are selected but filters have been saved before,
    // we should still enable the save button to allow clearing all filters
    return this.hasFiltersBeenSavedBefore && this.selectedFilterCount > 0;
  }

  private saveFilterOptions(): void {
    this.updateSelectedFilterCount();

    this.hasFiltersBeenSavedBefore = true;

    this.applyFilterOptions();

    this.showFilterView = false;
  }

  hasFilterChanges(): boolean {
    if (!this.filterOptions) {
      return false;
    }

    return this.filterOptions.some(option => option.selected);
  }

  clearAllFilters() {
    this.filterOptions.forEach(option => (option.selected = false));
  }

  private updateSelectedFilterCount() {
    this.selectedFilterCount = this.filterOptions.filter(
      o => o.selected,
    ).length;
  }

  resetFilterView(): void {
    this.showFilterView = false;
  }

  onProductClicked(product: Product): void {
    this.aiListService.setSelectedAIListSearchResult(product.productNumber);
  }

  resetMenuState(): void {
    this.searchQuery = '';
    this.showFilterView = false;
    this.selectedFilterCount = 0;
    this.isBestSellingSearch = true;
    this.clearAllFilters();
    this.saveFilterOptions();
    this.hasFiltersBeenSavedBefore = false;
  }
}
